import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { useMemo } from "react";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import {
	DataTable,
	DataTableColumnHeader,
	DataTableToolbar,
	DataTableFilterText,
	DataTableFilterFaceted,
	type FilterOption,
} from "@/components/data-table";
import { normalizeString } from "@/lib/utils";

export const Route = createFileRoute("/admin/inscriptions/")({
	component: InscriptionsPage,
});

interface TimeslotColumn {
	enumValue: string;
	displayName: string;
}

interface InscriptionRow {
	enrollmentId: string;
	studentId: string;
	className: string | null;
	studentLastName: string;
	studentFirstName: string;
	timeSlotDisplayName: string;
	timeSlotEnum: string;
	teacherName: string;
	school: string;
	parentPhone: string;
}

interface InscriptionListData {
	timeSlotColumns: TimeslotColumn[];
	inscriptions: InscriptionRow[];
}

function InscriptionsPage() {
	const { data, isLoading } = useQuery<InscriptionListData>({
		queryKey: ["inscriptions"],
		queryFn: async () => {
			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/api/admin/courses/inscriptions`,
				{
					headers: {
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				},
			);
			if (!response.ok)
				throw new Error("Erreur lors du chargement des inscriptions");
			return response.json();
		},
	});

	const inscriptions = data?.inscriptions ?? [];
	const timeSlotColumns = data?.timeSlotColumns ?? [];

	const schoolOptions: FilterOption[] = useMemo(() => {
		const counts = new Map<string, number>();
		for (const row of inscriptions) {
			counts.set(row.school, (counts.get(row.school) ?? 0) + 1);
		}
		return [...counts.entries()]
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([school, count]) => ({
				label: school,
				value: school,
				count,
			}));
	}, [inscriptions]);

	const timeSlotOptions: FilterOption[] = useMemo(() => {
		const counts = new Map<string, number>();
		for (const row of inscriptions) {
			counts.set(
				row.timeSlotEnum,
				(counts.get(row.timeSlotEnum) ?? 0) + 1,
			);
		}
		return timeSlotColumns.map((ts) => ({
			label: ts.displayName,
			value: ts.enumValue,
			count: counts.get(ts.enumValue) ?? 0,
		}));
	}, [inscriptions, timeSlotColumns]);

	const columns = useMemo<ColumnDef<InscriptionRow, any>[]>(() => {
		const baseCols: ColumnDef<InscriptionRow, any>[] = [
			{
				id: "className",
				accessorFn: (row) => row.className ?? "-",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Classe" />
				),
				cell: ({ row }) => (
					<span>{row.original.className || "-"}</span>
				),
				sortDescFirst: false,
				meta: { label: "Classe" },
			},
			{
				id: "student",
				accessorFn: (row) =>
					row.studentLastName + " " + row.studentFirstName,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Élève" />
				),
				cell: ({ row }) => (
					<div>
						<span className="font-medium">
							{row.original.studentLastName}
						</span>{" "}
						{row.original.studentFirstName}
					</div>
				),
				filterFn: (row, _columnId, filterValue: string) => {
					const searchValue = normalizeString(filterValue);
					const fullName = normalizeString(
						`${row.original.studentFirstName} ${row.original.studentLastName}`,
					);
					const reverseName = normalizeString(
						`${row.original.studentLastName} ${row.original.studentFirstName}`,
					);
					const className = normalizeString(
						row.original.className ?? "",
					);
					const teacher = normalizeString(
						row.original.teacherName ?? "",
					);
					return (
						fullName.includes(searchValue) ||
						reverseName.includes(searchValue) ||
						className.includes(searchValue) ||
						teacher.includes(searchValue)
					);
				},
				sortDescFirst: false,
				meta: { label: "Élève" },
			},
			{
				id: "timeSlot",
				accessorFn: (row) => row.timeSlotEnum,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Horaire" />
				),
				cell: ({ row }) => (
					<span className="text-xs">
						{row.original.timeSlotDisplayName}
					</span>
				),
				filterFn: (row, _id, value: string[]) =>
					value.includes(row.original.timeSlotEnum),
				meta: {
					label: "Horaire",
					textValue: (row: InscriptionRow) => row.timeSlotDisplayName,
				},
			},
			{
				id: "teacher",
				accessorKey: "teacherName",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Prof" />
				),
				cell: ({ row }) => (
					<span className="text-xs">
						{row.original.teacherName || "-"}
					</span>
				),
				meta: { label: "Prof" },
			},
			{
				id: "school",
				accessorKey: "school",
				header: ({ column }) => (
					<DataTableColumnHeader
						column={column}
						title="Établissement"
					/>
				),
				cell: ({ row }) => (
					<span className="text-xs">{row.original.school}</span>
				),
				filterFn: (row, _id, value: string[]) =>
					value.includes(row.original.school),
				meta: { label: "Établissement" },
			},
			{
				id: "parentPhone",
				accessorKey: "parentPhone",
				header: "Tél. parent",
				cell: ({ row }) => (
					<span className="text-xs">
						{row.original.parentPhone || "-"}
					</span>
				),
				enableSorting: false,
				meta: { label: "Tél. parent" },
			},
		];

		const slotCols: ColumnDef<InscriptionRow, any>[] = timeSlotColumns.map(
			(ts) => ({
				id: `slot_${ts.enumValue}`,
				accessorFn: (row: InscriptionRow) =>
					row.timeSlotEnum === ts.enumValue ? "X" : "",
				header: () => (
					<span className="text-xs whitespace-nowrap">
						{ts.displayName}
					</span>
				),
				cell: ({ row }: { row: any }) =>
					row.original.timeSlotEnum === ts.enumValue ? (
						<div className="w-6 h-6 mx-auto rounded bg-green-500 text-white flex items-center justify-center text-xs font-bold">
							X
						</div>
					) : null,
				enableSorting: false,
				meta: { label: ts.displayName },
			}),
		);

		return [...baseCols, ...slotCols];
	}, [timeSlotColumns]);

	const handleExportCsv = () => {
		if (!data || inscriptions.length === 0) {
			toast.error("Aucune donnée à exporter");
			return;
		}

		const headers = [
			"Classe",
			"Nom",
			"Prénom",
			"Horaire",
			"Prof",
			"Établissement",
			"Téléphone du parent",
			...timeSlotColumns.map((ts) => ts.displayName),
		];

		const csvRows = [headers.join(";")];

		for (const row of inscriptions) {
			const cells = [
				row.className || "",
				row.studentLastName,
				row.studentFirstName,
				row.timeSlotDisplayName,
				row.teacherName,
				row.school,
				row.parentPhone,
				...timeSlotColumns.map((ts) =>
					ts.enumValue === row.timeSlotEnum ? "X" : "",
				),
			];
			csvRows.push(cells.join(";"));
		}

		const csvContent = "\uFEFF" + csvRows.join("\n");
		const blob = new Blob([csvContent], {
			type: "text/csv;charset=utf-8;",
		});
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `inscriptions_${new Date().toISOString().split("T")[0]}.csv`;
		document.body.appendChild(a);
		a.click();
		window.URL.revokeObjectURL(url);
		document.body.removeChild(a);
		toast.success("Fichier CSV téléchargé avec succès");
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto" />
					<p className="mt-4 text-gray-600">
						Chargement des inscriptions...
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<div>
					<h1 className="text-3xl font-bold">Inscriptions</h1>
					<p className="text-gray-600 mt-2">
						Liste de toutes les inscriptions actives aux cours
					</p>
				</div>
				<Button onClick={handleExportCsv} variant="outline" size="sm">
					<Download className="mr-2 h-4 w-4" />
					Exporter CSV
				</Button>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>
						Inscriptions ({inscriptions.length})
					</CardTitle>
					<CardDescription>
						{inscriptions.length} inscription(s) active(s)
					</CardDescription>
				</CardHeader>
				<CardContent>
					<DataTable
						columns={columns}
						data={inscriptions}
						emptyMessage="Aucune inscription trouvée"
						toolbar={(table) => (
							<DataTableToolbar table={table} showExportExcel exportFilename="inscriptions">
								<DataTableFilterText
									column={table.getColumn("student")!}
									placeholder="Rechercher (nom, classe, prof...)"
								/>
								{table.getColumn("school") && (
									<DataTableFilterFaceted
										column={table.getColumn("school")!}
										title="Établissement"
										options={schoolOptions}
									/>
								)}
								{table.getColumn("timeSlot") && (
									<DataTableFilterFaceted
										column={table.getColumn("timeSlot")!}
										title="Créneau"
										options={timeSlotOptions}
									/>
								)}
							</DataTableToolbar>
						)}
					/>
				</CardContent>
			</Card>
		</div>
	);
}
